import React, { useState, useCallback } from 'react';
import { useDropzone, FileRejection, FileError, DropEvent } from 'react-dropzone';
import apiClient from '../../api/apiClient';

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  requirementId: string | number;
  onUploadSuccess?: () => void;
}

const UploadModal: React.FC<UploadModalProps> = ({
  isOpen,
  onClose,
  requirementId,
  onUploadSuccess
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  // File validation
  const fileValidation = useCallback((file: File) => {
    // Check file size
    if (file.size > 25 * 1024 * 1024) { // 25MB
      return {
        code: 'file-too-large',
        message: `File is too large. Maximum size is 25MB.`
      };
    }

    // Check file type (allow PDF, PNG, JPEG, JPG)
    const acceptedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!acceptedTypes.includes(file.type)) {
      return {
        code: 'file-invalid-type',
        message: `File type not supported. Please upload PDF, PNG, JPEG, or JPG.`
      };
    }

    return null;
  }, []);

  // Handle file drop
  const onDrop = useCallback((acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => {
    // Display errors for rejected files
    if (fileRejections.length > 0) {
      const errors = fileRejections.map(rejected => {
        return rejected.errors.map((error: FileError) => error.message).join(', ');
      }).join('. ');
      setError(errors);
      return;
    }

    // Limit total files to 25
    if (files.length + acceptedFiles.length > 25) {
      setError(`You can only upload up to 25 files in total.`);
      // Add only enough files to reach the limit
      const remainingSlots = 25 - files.length;
      if (remainingSlots > 0) {
        setFiles(prev => [...prev, ...acceptedFiles.slice(0, remainingSlots)]);
      }
      return;
    }

    // Add files to state
    setFiles(prev => [...prev, ...acceptedFiles]);
    setError(null);
  }, [files]);

  // Configure dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    validator: fileValidation,
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
  });

  // Handle file removal
  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  // Handle submit - upload all files
  const handleSubmit = async () => {
    if (files.length === 0) {
      setError('Please select at least one file to upload.');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Create FormData for each file and upload
      const uploadPromises = files.map(file => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('id', requirementId.toString());
        
        // Custom headers for multipart/form-data
        return apiClient.post('/media_upload', formData, {
          'Content-Type': 'multipart/form-data',
        });
      });

      // Wait for all uploads to complete
      await Promise.all(uploadPromises);
      
      setUploadSuccess(true);
      if (onUploadSuccess) {
        onUploadSuccess();
      }
      // Close modal after success (with slight delay for feedback)
      setTimeout(() => {
        onClose();
        setFiles([]);
        setUploadSuccess(false);
      }, 1500);
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err instanceof Error ? err.message : 'File upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // If modal is not open, don't render anything
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>
        
        {/* Modal content */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full mx-auto p-6 z-10">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-neutral-800">Upload Documents</h3>
            <button 
              onClick={onClose}
              className="text-neutral-500 hover:text-neutral-800 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Dropzone */}
          <div 
            {...getRootProps()} 
            className={`border-2 border-dashed rounded-lg p-6 mb-4 text-center cursor-pointer transition-colors ${
              isDragActive 
                ? 'border-primary-500 bg-primary-50' 
                : 'border-neutral-300 hover:border-primary-400 hover:bg-neutral-50'
            }`}
          >
            <input {...getInputProps()} />
            <div className="flex flex-col items-center justify-center py-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-neutral-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-sm text-neutral-600 mb-1">Drag and drop files here, or click to select files</p>
              <p className="text-xs text-neutral-500">
                Supported formats: PDF, PNG, JPEG, JPG (max 25 files, 25MB each)
              </p>
            </div>
          </div>
          
          {/* Error message */}
          {error && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-md text-sm mb-4">
              {error}
            </div>
          )}
          
          {/* Success message */}
          {uploadSuccess && (
            <div className="bg-green-50 text-green-600 px-4 py-3 rounded-md text-sm mb-4">
              Files successfully uploaded!
            </div>
          )}
          
          {/* File list */}
          {files.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-neutral-800 mb-2">Selected Files ({files.length}/25)</h4>
              <div className="max-h-40 overflow-y-auto">
                {files.map((file, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-neutral-100 last:border-b-0">
                    <div className="flex items-center">
                      <div className="mr-2">
                        {file.type.includes('pdf') ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        )}
                      </div>
                      <div>
                        <div className="text-sm text-neutral-800 truncate max-w-xs">{file.name}</div>
                        <div className="text-xs text-neutral-500">{(file.size / (1024 * 1024)).toFixed(2)} MB</div>
                      </div>
                    </div>
                    <button 
                      onClick={() => removeFile(index)}
                      className="text-neutral-400 hover:text-red-500 transition-colors"
                      disabled={uploading}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Actions */}
          <div className="flex justify-end mt-6">
            <button
              onClick={onClose}
              className="text-sm px-4 py-2 mr-2 border border-neutral-300 rounded-lg hover:bg-neutral-50 text-neutral-800 transition-colors"
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="text-sm px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg shadow-button transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={files.length === 0 || uploading}
            >
              {uploading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Uploading...
                </div>
              ) : (
                `Upload ${files.length > 0 ? `(${files.length})` : ''}`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;